import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  STUDY_FINISHED_MESSAGE,
  STUDY_IFRAME_ALLOW,
  STUDY_RETURN_ENDPOINT,
  STUDY_STATUS_SCREENOUT,
  STUDY_TEXT,
  studyLinkPath,
} from '../../constants/eyetrackingConstants';
import { readRid } from './usePipedMedia';
import { isWithinFrame } from './studyFrame';

const API_URL = import.meta.env.VITE_API_URL || '';

interface EyetrackingStudyFrameProps {
  surveyId: number;
  /** The study's key in the survey's settings block. */
  study: string;
  /** Accessible name for the frame (no vendor name). */
  label: string;
  /** The study finished normally: move the survey on, with the stored study data as the answer. */
  onFinished: (answer: StudyAnswer) => void;
  /** The survey's own error after it was handed the answer: show retry instead of the spinner. */
  submitError?: string | null;
}

type FrameState =
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'saving' }
  | { status: 'error' };

/** The study's returned data, as the backend stored it on the study screen. */
export type StudyAnswer = Record<string, unknown>;

interface ReturnResponse {
  status: string;
  answer: StudyAnswer;
  redirect_url?: string;
}

/**
 * Runs one eye-tracking study in a full-screen iframe. The study's Finish page posts its URL
 * params here (the study drops our own params in iframe mode); this posts them back with the
 * survey, respondent id and study key (the backend stores them as this screen's answer), then advances with
 * that answer, or leaves for the panel screen-out link when the study ended the respondent early.
 */
const EyetrackingStudyFrame: React.FC<EyetrackingStudyFrameProps> = ({
  surveyId,
  study,
  label,
  onFinished,
  submitError,
}) => {
  const [state, setState] = useState<FrameState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const handledRef = useRef(false);
  const handedOffRef = useRef(false);
  /** The finished study's params, kept so a failed save is retried without redoing the study. */
  const finishedQueryRef = useRef<string | null>(null);
  const rid = readRid();

  useEffect(() => {
    if (!rid) {
      setState({ status: 'error' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetch(`${API_URL}${studyLinkPath(surveyId, rid, study)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`study link ${res.status}`))))
      .then((body: { url: string }) => !cancelled && setState({ status: 'ready', url: body.url }))
      .catch(() => !cancelled && setState({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [surveyId, rid, study, attempt]);

  const submitReturn = useCallback(
    async (query: string) => {
      handedOffRef.current = false;
      setState({ status: 'saving' });
      try {
        const res = await fetch(`${API_URL}${STUDY_RETURN_ENDPOINT}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            survey_id: surveyId,
            rid,
            study,
            query: Object.fromEntries(new URLSearchParams(query)),
          }),
        });
        if (!res.ok) throw new Error(`study return ${res.status}`);
        const body: ReturnResponse = await res.json();
        if (body.status === STUDY_STATUS_SCREENOUT && body.redirect_url) {
          window.location.assign(body.redirect_url);
          return;
        }
        handedOffRef.current = true;
        onFinished(body.answer);
      } catch {
        setState({ status: 'error' });
      }
    },
    [surveyId, rid, study, onFinished],
  );

  useEffect(() => {
    if (submitError && handedOffRef.current) setState({ status: 'error' });
  }, [submitError]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const frameWindow = frameRef.current?.contentWindow;
      if (!frameWindow || !isWithinFrame(event.source, frameWindow)) return;
      if (event.data?.type !== STUDY_FINISHED_MESSAGE || typeof event.data.query !== 'string')
        return;
      if (handledRef.current) return;
      handledRef.current = true;
      finishedQueryRef.current = event.data.query;
      void submitReturn(event.data.query);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [submitReturn]);

  if (state.status === 'error') {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center" role="alert">
        <p className="text-gray-700">{STUDY_TEXT.error}</p>
        <button
          type="button"
          className="rounded-md bg-teal-600 px-4 py-2 text-white hover:bg-teal-700"
          onClick={() =>
            finishedQueryRef.current !== null
              ? void submitReturn(finishedQueryRef.current)
              : setAttempt((n) => n + 1)
          }
        >
          {STUDY_TEXT.retry}
        </button>
      </div>
    );
  }

  if (state.status !== 'ready') {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-gray-600" role="status">
        <Loader2 className="h-5 w-5 animate-spin" />
        {state.status === 'saving' ? STUDY_TEXT.saving : STUDY_TEXT.loading}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <iframe
        ref={frameRef}
        src={state.url}
        title={label}
        allow={STUDY_IFRAME_ALLOW}
        allowFullScreen
        className="h-full w-full border-0"
        data-testid="eyetracking-study-frame"
      />
    </div>
  );
};

export default EyetrackingStudyFrame;
